import { useCallback, useEffect, useState } from 'react';

interface OptimisticSelection<Value> {
  value: Value;
  key: unknown;
}

interface UseOptimisticTabSelectionOptions<Value> {
  committedValue: Value;
  // Keep this identity stable between commits; keys are compared by reference/value equality.
  committedKey?: unknown;
}

export const useOptimisticTabSelection = <Value>(options: UseOptimisticTabSelectionOptions<Value>) => {
  const { committedValue } = options;
  const committedKey = options.committedKey === undefined ? committedValue : options.committedKey;
  const [optimisticSelection, setOptimisticSelection] = useState<OptimisticSelection<Value> | null>(null);
  const displayedValue =
    optimisticSelection && optimisticSelection.key === committedKey ? optimisticSelection.value : committedValue;
  const hasPendingSelection =
    optimisticSelection !== null &&
    optimisticSelection.key === committedKey &&
    optimisticSelection.value !== committedValue;

  useEffect(() => {
    setOptimisticSelection((current) => (current?.key === committedKey ? current : null));
  }, [committedKey]);

  const select = useCallback(
    (value: Value) => {
      setOptimisticSelection({ value, key: committedKey });
    },
    [committedKey]
  );

  const reset = useCallback(() => {
    setOptimisticSelection(null);
  }, []);

  return { displayedValue, hasPendingSelection, select, reset };
};
